import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Palette, Trophy, Target, ChevronRight, Radio } from "lucide-react";
import { formatMatchDate } from "@/lib/dates";

const PLACEHOLDER_CREST = "https://placehold.co/150x150/09090b/FFF?text=FC";

export const TeamHeader = ({ team, primary, others, standing }) => {
  const photo = team.team_photo_url;
  return (
    <section className="relative overflow-hidden bg-[#09090b] text-white" data-testid="team-header">
      {photo ? (
        <img src={photo} alt={`${team.name} team photo`} className="absolute inset-0 w-full h-full object-cover object-[center_30%]" loading="eager" data-testid="team-header-photo" />
      ) : (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(0,158,96,0.35),transparent_55%),radial-gradient(circle_at_10%_90%,rgba(255,199,44,0.18),transparent_50%)]" data-testid="team-header-fallback">
          <img src={team.logo_url || PLACEHOLDER_CREST} alt="" aria-hidden className="absolute -right-16 top-1/2 -translate-y-1/2 w-[28rem] h-[28rem] object-contain opacity-[0.07]" />
        </div>
      )}
      <div className={`absolute inset-0 ${photo ? "bg-gradient-to-t from-black via-black/70 to-black/20" : ""}`} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-10 pb-12 md:pt-14 md:pb-16 min-h-[340px] md:min-h-[420px] flex flex-col justify-end">
        <Link to="/teams" className="absolute top-6 left-4 md:left-8 inline-flex items-center text-sm text-gray-300 hover:text-white transition-colors" data-testid="back-to-teams">
          <ArrowLeft className="w-4 h-4 mr-2" /> All Clubs
        </Link>
        <div className="flex flex-col md:flex-row md:items-end gap-6">
          <div className="w-28 h-28 md:w-36 md:h-36 bg-white p-3 flex items-center justify-center shadow-2xl flex-shrink-0">
            <img src={team.logo_url || PLACEHOLDER_CREST} alt={`${team.name} crest`} className="max-w-full max-h-full object-contain" data-testid="team-header-crest" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {primary && (
                <Link to={`/competitions/${primary.competition_id}`} className="px-3 py-1 bg-[#FFC72C] text-black text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors" data-testid="team-header-division">
                  {primary.name}
                </Link>
              )}
              {primary?.season && <span className="px-3 py-1 border border-white/30 text-xs font-bold uppercase tracking-widest" data-testid="team-header-season">{primary.season} Season</span>}
              {others.map(c => (
                <Link key={c.competition_id} to={`/competitions/${c.competition_id}`} className="px-3 py-1 bg-white/10 text-xs font-semibold uppercase tracking-widest hover:bg-white/20 transition-colors" data-testid={`team-header-other-${c.competition_id}`}>
                  {c.name}
                </Link>
              ))}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none" data-testid="team-header-name">{team.name}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-sm text-gray-300">
              {team.stadium && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{team.stadium}</span>}
              {team.founded && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />Founded {team.founded}</span>}
              {team.colors && <span className="flex items-center gap-1.5"><Palette className="w-4 h-4" />{team.colors}</span>}
            </div>
          </div>
          {standing?.played > 0 && standing.position && (
            <div className="text-left md:text-right" data-testid="team-header-position">
              <p className="text-[10px] uppercase tracking-widest text-gray-400">League position</p>
              <p className="font-heading text-6xl font-black text-[#FFC72C] leading-none">{standing.position}</p>
              <p className="text-xs text-gray-400 mt-1">{standing.points} pts · {standing.played} played</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const SeasonStats = ({ standing }) => {
  if (!standing || !standing.played) {
    return <p className="text-sm text-gray-500" data-testid="season-stats-empty">Season statistics will appear once this club's first result is recorded.</p>;
  }
  const items = [["Played", standing.played], ["Won", standing.won], ["Drawn", standing.drawn], ["Lost", standing.lost],
    ["Goals For", standing.goals_for], ["Goals Against", standing.goals_against], ["Goal Diff", standing.goal_difference > 0 ? `+${standing.goal_difference}` : standing.goal_difference], ["Points", standing.points]];
  return (
    <div data-testid="season-stats">
      <div className="grid grid-cols-4 md:grid-cols-8 border border-gray-200 bg-white">
        {items.map(([label, val]) => (
          <div key={label} className="p-3 text-center border-r border-b md:border-b-0 border-gray-100 last:border-r-0">
            <p className="font-heading text-2xl font-black text-[#09090b]" data-testid={`season-stat-${label.toLowerCase().replace(/ /g, "-")}`}>{val}</p>
            <p className="text-[10px] uppercase tracking-widest text-gray-400">{label}</p>
          </div>
        ))}
      </div>
      {standing.form?.length > 0 && (
        <div className="flex items-center gap-2 mt-3" data-testid="season-form">
          <span className="text-[10px] uppercase tracking-widest text-gray-400 mr-1">Form</span>
          {standing.form.map((f, i) => <span key={i} className={`form-badge form-badge-${f.toLowerCase()}`}>{f}</span>)}
        </div>
      )}
    </div>
  );
};

export const TopScorer = ({ leaders, teamName, competition }) => (
  <div className="bg-[#09090b] text-white" data-testid="team-top-scorer">
    <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
      <Target className="w-4 h-4 text-[#FFC72C]" />
      <h2 className="font-heading font-bold uppercase tracking-wide">Team Top Scorer</h2>
      {competition && <span className="ml-auto text-[10px] uppercase tracking-widest text-gray-400">{competition.name} {competition.season}</span>}
    </div>
    {leaders.length === 0 ? (
      <p className="px-5 py-8 text-sm text-gray-400" data-testid="top-scorer-empty">Top scorer information will appear when verified match data is available.</p>
    ) : (
      <div className={`grid ${leaders.length > 1 ? "sm:grid-cols-2" : ""} divide-y sm:divide-y-0 sm:divide-x divide-white/10`}>
        {leaders.length > 1 && <p className="sm:col-span-2 px-5 pt-3 text-xs font-bold uppercase tracking-widest text-[#FFC72C]" data-testid="top-scorer-tie">Joint leaders · {leaders.length} players tied</p>}
        {leaders.map(p => (
          <Link key={p.player_id} to={`/players/${p.player_id}`} className="flex items-center gap-4 p-5 hover:bg-white/5 transition-colors group" data-testid={`top-scorer-${p.player_id}`}>
            <div className="w-20 h-24 bg-white/10 overflow-hidden flex-shrink-0">
              {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-heading text-2xl text-white/30">{p.number ?? ""}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-xl font-bold uppercase leading-tight group-hover:text-[#FFC72C] transition-colors">{p.name}</p>
              <p className="text-xs text-gray-400 mt-1">{teamName}{p.position ? ` · ${p.position}` : ""}</p>
            </div>
            <div className="text-right">
              <p className="font-heading text-5xl font-black text-[#FFC72C] leading-none" data-testid={`top-scorer-goals-${p.player_id}`}>{p.goals}</p>
              <p className="text-[10px] uppercase tracking-widest text-gray-400">Goals</p>
            </div>
          </Link>
        ))}
      </div>
    )}
  </div>
);

const STATUS_STYLE = {
  live: "bg-red-600 text-white", completed: "bg-gray-900 text-white", scheduled: "bg-[#FFC72C] text-black",
};

export const TeamMatchCard = ({ match, teamId, teamsMap, compsMap }) => {
  const isHome = match.home_team_id === teamId;
  const opp = teamsMap[isHome ? match.away_team_id : match.home_team_id];
  const done = match.status === "completed";
  const us = isHome ? match.home_score : match.away_score;
  const them = isHome ? match.away_score : match.home_score;
  const res = done && us != null && them != null ? (us > them ? "W" : us < them ? "L" : "D") : null;
  const comp = compsMap[match.competition_id];
  const label = `${isHome ? "Home" : "Away"} vs ${opp?.name || "opponent"}, ${formatMatchDate(match.match_date, { month: "long", day: "numeric", year: "numeric" })}, ${done ? `final score ${us} - ${them}` : match.status}. Open Match Centre`;
  return (
    <Link to={`/matches/${match.match_id}`} aria-label={label}
      className={`flex items-center gap-3 md:gap-4 p-3 md:p-4 bg-white border border-gray-200 border-l-4 hover:bg-gray-50 hover:border-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#009E60] transition-colors ${res === "W" ? "border-l-[#009E60]" : res === "L" ? "border-l-red-500" : res === "D" ? "border-l-gray-400" : match.status === "live" ? "border-l-red-600" : "border-l-[#FFC72C]"}`}
      data-testid={`team-match-${match.match_id}`}>
      <div className="w-16 md:w-24 flex-shrink-0 text-xs text-gray-500">
        <p className="font-semibold text-[#09090b]">{formatMatchDate(match.match_date, { month: "short", day: "numeric" })}</p>
        <p>{done ? formatMatchDate(match.match_date, { year: "numeric" }) : match.match_time || "TBD"}</p>
      </div>
      <span className={`w-6 text-center text-[10px] font-bold uppercase py-0.5 ${isHome ? "bg-[#009E60] text-white" : "bg-gray-200"}`}>{isHome ? "H" : "A"}</span>
      <img src={opp?.logo_url || PLACEHOLDER_CREST} alt="" className="w-8 h-8 object-contain" />
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold uppercase truncate">{opp?.name || "TBD"}</p>
        <p className="text-[11px] text-gray-400 truncate">{comp?.name}{match.matchday ? ` · MD ${match.matchday}` : ""}{match.round ? ` · ${match.round}` : ""}</p>
      </div>
      {done ? (
        <div className="flex items-center gap-2">
          {res && <span className={`form-badge form-badge-${res.toLowerCase()}`}>{res}</span>}
          <span className="font-heading font-black text-xl md:text-2xl tabular-nums">{us}-{them}</span>
        </div>
      ) : (
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 ${STATUS_STYLE[match.status] || "bg-gray-200 text-gray-700"}`} data-testid={`team-match-status-${match.match_id}`}>
          {match.status === "live" && <Radio className="w-3 h-3 animate-pulse" />}{match.status}
          {match.status === "live" && match.home_score != null && ` ${us}-${them}`}
        </span>
      )}
      <ChevronRight className="w-4 h-4 text-gray-300 hidden sm:block" />
    </Link>
  );
};

export const StandingsPanel = ({ rows, teamId, competition }) => {
  if (!competition) return null;
  const computed = rows.some(r => r.position);
  return (
    <div className="bg-white border border-gray-200" data-testid="team-standings-panel">
      <div className="px-4 py-3 bg-[#09090b] text-white flex items-center gap-2">
        <Trophy className="w-4 h-4 text-[#FFC72C]" />
        <h2 className="font-heading font-bold uppercase text-sm tracking-wide flex-1 truncate">{competition.name}</h2>
        <span className="text-[10px] text-gray-400">{competition.season}</span>
      </div>
      {rows.length === 0 || !computed ? (
        <p className="p-4 text-sm text-gray-500" data-testid="team-standings-empty">Standings will appear once this competition's table has been calculated.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200">
              <th className="py-2 pl-3 text-left w-7">#</th><th className="text-left">Team</th>
              <th className="text-center w-7">P</th><th className="text-center w-7 hidden xl:table-cell">W</th>
              <th className="text-center w-7 hidden xl:table-cell">D</th><th className="text-center w-7 hidden xl:table-cell">L</th>
              <th className="text-center w-8">GD</th><th className="text-center w-9 pr-3">Pts</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const me = r.team_id === teamId;
              return (
                <tr key={r.team_id} className={`border-b border-gray-100 last:border-0 ${me ? "bg-[#009E60]/10 font-bold" : "hover:bg-gray-50"}`} data-testid={me ? "standings-row-current" : `standings-row-${r.team_id}`} aria-current={me ? "true" : undefined}>
                  <td className={`py-2 pl-3 ${me ? "border-l-4 border-[#009E60] pl-2" : ""}`}>{r.position}</td>
                  <td className="py-2">
                    <Link to={`/teams/${r.team_id}`} className="flex items-center gap-2 hover:text-[#009E60] transition-colors">
                      <img src={r.logo_url || PLACEHOLDER_CREST} alt="" className="w-4 h-4 object-contain" />
                      <span className="truncate max-w-[110px]">{r.team_name}</span>
                    </Link>
                  </td>
                  <td className="text-center">{r.played}</td>
                  <td className="text-center hidden xl:table-cell">{r.won}</td>
                  <td className="text-center hidden xl:table-cell">{r.drawn}</td>
                  <td className="text-center hidden xl:table-cell">{r.lost}</td>
                  <td className="text-center">{r.goal_difference > 0 ? `+${r.goal_difference}` : r.goal_difference}</td>
                  <td className="text-center pr-3 font-bold">{r.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <Link to={`/competitions/${competition.competition_id}`} className="block text-center py-3 text-xs font-bold uppercase tracking-widest text-[#009E60] border-t border-gray-200 hover:bg-gray-50 transition-colors" data-testid="view-full-standings">
        View Full Standings
      </Link>
    </div>
  );
};

export const DivisionTeams = ({ teams, competition }) => {
  if (!competition || teams.length === 0) return null;
  return (
    <section className="py-14 px-4 md:px-8 bg-[#09090b]" data-testid="division-teams">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#FFC72C] font-bold">{competition.name} · {competition.season}</p>
            <h2 className="font-heading text-2xl md:text-3xl font-black uppercase text-white">More Teams in This Division</h2>
          </div>
          <Link to={`/competitions/${competition.competition_id}`} className="text-xs font-bold uppercase tracking-widest text-gray-300 hover:text-white whitespace-nowrap" data-testid="division-view-all">View All Teams</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-3 snap-x md:grid md:grid-cols-4 lg:grid-cols-6 md:overflow-visible">
          {teams.map(t => (
            <Link key={t.team_id} to={`/teams/${t.team_id}`} className="snap-start flex-shrink-0 w-36 md:w-auto bg-white/5 border border-white/10 p-4 text-center hover:bg-white/10 hover:border-[#FFC72C] transition-colors group" data-testid={`division-team-${t.team_id}`}>
              <img src={t.logo_url || PLACEHOLDER_CREST} alt="" className="w-14 h-14 object-contain mx-auto mb-3 bg-white p-1 group-hover:scale-105 transition-transform" />
              <p className="font-heading font-bold uppercase text-sm text-white leading-tight line-clamp-2">{t.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">{t.position ? `${t.position}${["th","st","nd","rd"][(t.position % 100 > 10 && t.position % 100 < 14) ? 0 : (t.position % 10 < 4 ? t.position % 10 : 0)]} in table` : competition.name}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
